<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'
import { Head, Link } from '@inertiajs/vue3'
import InternalLayout from '@/layouts/InternalLayout.vue'

let jsonLdScript: HTMLScriptElement | null = null

onMounted(() => {
  jsonLdScript = document.createElement('script')
  jsonLdScript.type = 'application/ld+json'
  jsonLdScript.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'Quality Managers',
    description: 'Meet the Mow Managers Quality Managers — experienced lawn care professionals you can trust to take great care of your lawn.',
    url: 'https://mowmanagers.com/managers',
    mainEntity: {
      '@type': 'Organization',
      name: 'Mow Managers',
      url: 'https://mowmanagers.com',
      employee: [
        { '@type': 'Person', name: 'Jose Garcia', jobTitle: 'Quality Manager', image: 'https://mowmanagers.com/img/manager-jose.jpg' },
        { '@type': 'Person', name: 'Maximo Garcia', jobTitle: 'Quality Manager', image: 'https://mowmanagers.com/img/manager-maximo.jpg' },
        { '@type': 'Person', name: 'Humberto Ugalde', jobTitle: 'Quality Manager', image: 'https://mowmanagers.com/img/manager-humberto.jpg' },
      ],
    },
  })
  document.head.appendChild(jsonLdScript)
})

onUnmounted(() => {
  jsonLdScript?.remove()
})

const managers = [
  {
    name: 'Jose Garcia',
    title: 'Quality Manager',
    image: '/img/manager-jose.jpg',
    bio: 'Jose has been an integral part of the Mow Managers Team since the beginning. Before joining Mow Managers, he was involved in the management and operations of several other Lawn Care, Mowing Maintenance and Landscaping businesses. Jose brings over 20 years of experience and first-hand knowledge of Turfgrass Management, for the benefit of his customers. He is also a seasoned veteran administrator and highly astute with the management of routes and human resources.',
  },
  {
    name: 'Maximo Garcia',
    title: 'Quality Manager',
    image: '/img/manager-maximo.jpg',
    bio: 'Maximo has also been a long time member of the Mow Managers Team. He has over 16 years of experience, serving in a variety of different functions from managing and resourcing lawn care and mowing equipment, to keeping the lawn care trucks moving and making sure crews meet their on-time obligations.',
  },
  {
    name: 'Humberto Ugalde',
    title: 'Quality Manager',
    image: '/img/manager-humberto.jpg',
    bio: 'Humberto is a highly skilled and talented member of our team. With over 8 years of experience in managing communications, he is also great at finding solutions due to his extensive work in customer service and construction.',
  },
]
</script>

<template>
  <InternalLayout title="Quality Managers" hero-image="/img/perfectly-striped-lawn-large-home.jpg">
    <Head title="Quality Managers">
      <meta head-key="description" name="description" content="Meet the Mow Managers Quality Managers — experienced lawn care professionals with over 20 years of experience you can trust to take great care of your lawn." />
      <link rel="canonical" href="https://mowmanagers.com/managers" />
      <meta property="og:title" content="Quality Managers - Mow Managers" />
      <meta property="og:description" content="Meet the Mow Managers Quality Managers — experienced lawn care professionals you can trust to take great care of your lawn." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://mowmanagers.com/managers" />
      <meta property="og:image" content="https://mowmanagers.com/img/managers-hero.jpg" />
      <meta property="og:image:alt" content="Mow Managers quality lawn care professionals" />
    </Head>

    <!-- ==================== INTRO ==================== -->
    <section aria-labelledby="intro-heading" class="py-20 lg:py-28 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div class="rounded-[20px] overflow-hidden">
            <img
              src="/img/managers-hero.jpg"
              alt="Mow Managers quality lawn care professionals at work"
              class="w-full h-full object-cover"
              width="640"
              height="480"
              loading="eager"
            />
          </div>

          <div>
            <p class="text-mm-green text-[21px] font-extrabold uppercase tracking-[4.2px]">
              Quality Managers
            </p>
            <h2 id="intro-heading" class="text-mm-dark text-3xl sm:text-4xl lg:text-[70px] font-bold lg:leading-[80px] mt-3">
              Lawn Care Pros<br />You Can Trust.
            </h2>
            <p class="text-[#6d706d] text-[20px] font-medium mt-6 leading-[34px] max-w-[628px]">
              At Mow Managers, our Quality Managers are experienced lawn care professionals that we trust to take great
              care of your lawn. We're committed to providing you with the best customer service, fast and reliable
              communication, and a freshly cut lawn that you can be proud of.
            </p>
            <Link
              href="/get-started"
              class="mt-8 inline-block bg-mm-green text-white font-bold uppercase tracking-[1.6px] px-8 py-3 rounded-md hover:bg-mm-green-dark transition-colors text-[16px] cursor-pointer"
            >
              Get Started
            </Link>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== TEAM MEMBERS ==================== -->
    <section aria-labelledby="team-heading" class="bg-mm-dark py-20 lg:py-28">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16 lg:mb-20">
          <p class="text-mm-green text-[21px] font-extrabold uppercase tracking-[4.2px]">
            Meet Our Team
          </p>
          <h2 id="team-heading" class="text-white text-3xl sm:text-4xl lg:text-[70px] font-bold lg:leading-[80px] mt-3">
            Our Quality Managers
          </h2>
        </div>

        <ul class="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10 list-none p-0" role="list">
          <li
            v-for="manager in managers"
            :key="manager.name"
          >
            <article class="bg-white rounded-[20px] overflow-hidden h-full">
              <img
                :src="manager.image"
                :alt="`${manager.name}, ${manager.title} at Mow Managers`"
                class="w-full aspect-square object-cover"
                width="400"
                height="400"
                loading="lazy"
              />
              <div class="p-6 lg:p-8">
                <h3 class="text-mm-dark text-[26px] font-bold">{{ manager.name }}</h3>
                <p class="text-mm-green text-[16px] font-bold uppercase tracking-[1.6px] mt-1" role="doc-subtitle">
                  {{ manager.title }}
                </p>
                <p class="text-[#6d706d] text-[16px] leading-[28px] mt-4">
                  {{ manager.bio }}
                </p>
              </div>
            </article>
          </li>
        </ul>
      </div>
    </section>

    <!-- ==================== CTA ==================== -->
    <section aria-labelledby="cta-heading" class="py-20 lg:py-28 bg-white">
      <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p class="text-mm-green text-[21px] font-extrabold uppercase tracking-[4.2px]">
          Ready to Get Started?
        </p>
        <h2 id="cta-heading" class="text-mm-dark text-3xl sm:text-4xl lg:text-[70px] font-bold lg:leading-[80px] mt-3">
          Get a Free<br />Price Quote
        </h2>
        <p class="text-[#6d706d] text-[20px] font-medium mt-6 leading-[34px] max-w-[628px] mx-auto">
          Enter your address and zip code to get an instant price quote. Our Quality Managers and their crews are ready
          to take care of your lawn.
        </p>
        <Link
          href="/get-started"
          class="mt-8 inline-block bg-mm-green text-white font-bold uppercase tracking-[1.6px] px-10 py-3 rounded-md hover:bg-mm-green-dark transition-colors text-[16px] cursor-pointer"
        >
          Free Price Quote
        </Link>
      </div>
    </section>
  </InternalLayout>
</template>
